import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Link from '@material-ui/core/Link';
import InputLabel from '@material-ui/core/InputLabel';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { useHistory } from 'react-router-dom';

import { formatAddress } from 'utilities/format';
// import Directions from 'components/Map/Directions';
import cache from 'utilities/cache';
import CustomAuthDialog from 'components/Auth/CustomAuthDialog';
// import CreditCard from 'components/Payment/CreditCard';
import LinePayButton from 'components/Payment/LinePayButton';
import NewebPayButton from 'components/Payment/NewebPayButton';
import CashButton from 'components/Payment/CashButton';
import { request, asyncListAll } from 'utilities/graph';
import {
  quoteOrder, createElder, updateElder, createElderTag, individualClientCreateOrder, groupPurchaseClientQueryOrderAmount,
  getSupportedPaymentService,
} from 'graphql/mutations';
import { getEldersByClientByStatus, getUser, getRestaurant } from 'graphql/queries';
import { Colors } from '@silvergatedelivery/constants';
import moment from 'moment';
import InvoiceInput from './InvoiceInput';
import { TIME_ZONE } from '@silvergatedelivery/constants';
import { compareObjects } from 'utilities/sorting';
import { toastr } from 'react-redux-toastr';

const BackToMenuButton = ({ onStepBack, isLoading }) => (
  <Grid item xs={12} container justifyContent="space-evenly" style={{ paddingTop: 16 }}>
    <Button
      type='submit'
      color='primary'
      variant='contained'
      onClick={() => onStepBack()}
      disabled={isLoading}
      style={{ fontSize: '1.2rem' }}
    >
      <NavigateBeforeIcon />
      重新選擇餐點
    </Button>
  </Grid>
);

BackToMenuButton.propTypes = {
  onStepBack: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default function CompleteOrder({ onStepBack }) {
  const { t } = useTranslation();
  const history = useHistory();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeCancelInvoice, setAgreeCancelInvoice] = useState(false);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [invoice, setInvoice] = useState({
    type: 'individual',
    email: '',
    carrier: '',
    title: '',
    taxIdNumber: '',
  });

  const [date, setDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [restaurant, setRestaurant] = useState();
  const [supportedPaymentService, setSupportedPaymentService] = useState({});

  const getRestaurantInfo = async (restaurantId) => {
    const { data: { getRestaurant: restaurant } } = await request(getRestaurant, { id: restaurantId });
    setRestaurant(restaurant);
  };


  const updateOrder = (toUpdateProps = {}) => {
    // global.logger.debug('updateOrder', toUpdateProps);

    const updatedOrder = Object.assign(order, toUpdateProps);

    setOrder({ ...updatedOrder });
    cache.set('public:menu-order', updatedOrder); // TODO: 如果訂購者往前修改餐點，會導致訂單資料和快取中不一致（不影響送出訂單的正確性，因為送出時沒有使用到public:menu-order中的訂單資料）

    global.logger.debug('order', updatedOrder);
  };

  const updateOrderDate = (newDate) => {
    const toUpdateProps = {
      date: newDate,
      deliveryBy: moment(`${newDate} ${timeSlot.split('-')[0]}`).tz(TIME_ZONE).toISOString(),
    };
    updateOrder(toUpdateProps);
    setDate(newDate);
  };

  const updateOrderTimeSlot = (newTimeSlot) => {
    const toUpdateProps = {
      timeSlot: newTimeSlot,
      deliveryBy: moment(`${date} ${newTimeSlot.split('-')[0]}`).tz(TIME_ZONE).toISOString(),
    };
    updateOrder(toUpdateProps);
    setTimeSlot(newTimeSlot);
  };

  const checkCurrentOrderAmount = async () => {
    setIsLoading(true);
    // 檢查送出訂單當下餐廳的訂單預購量，以免同時有人下訂爆單
    if (restaurant && typeof restaurant.maxOrderQuantity === 'number' && restaurant.maxOrderQuantity > 0) {
      try {
        const { date: orderDate, timeSlot: orderTimeSlot } = cache.get('public:menu-elder');
        const currentMealsQuantity = order.mealItems.reduce((sum, item) => sum + item.quantity, 0);

        const startTime = orderTimeSlot.split('-')[0];
        const hour = parseInt(startTime.split(':')[0], 10);

        const { data: { groupPurchaseClientQueryOrderAmount: amountResult } } = await request(groupPurchaseClientQueryOrderAmount, {
          input: {
            restaurantId: restaurant.id,
            date: orderDate,
            mealSlot: hour < 15 ? 'lunch' : 'dinner',
          },
        }, 'API_KEY');

        const receivedOrderAmount = amountResult.totalQuantity || 0;

        if (receivedOrderAmount + currentMealsQuantity > restaurant.maxOrderQuantity) {
          // alert('訂單失敗：已超過餐廳訂單上限，請調整餐點數量或稍後再試。');
          toastr.error('訂單失敗', '已超過餐廳訂單上限，請調整餐點數量或稍後再試。');
          setIsLoading(false);
          return false;
        }
      } catch (error) {
        global.logger.error('Error checking restaurant order capacity:', error);
        // Optionally, decide if you want to halt the order or show a generic error
        // alert('檢查餐廳訂單預購量時發生錯誤，請稍後再試。');
        toastr.error('錯誤', '檢查餐廳訂單預購量時發生錯誤，請稍後再試。');
        setIsLoading(false);
        return false;
      }
    }
    setIsLoading(false);
    return true;
  };

  const handleComplete = async (transaction) => {
    setIsLoading(true);
    global.logger.debug({ transaction, order, invoice });

    const username = cache.get('app:username');
    let clientId = cache.get('app:clientId');
    if (!clientId) {
      const { data: { getUser: existingUser } } = await request(getUser, { username });
      clientId = existingUser.clientId;
    }

    const { id: transactionId } = transaction;

    const {
      orderQuoteId,
      restaurantId,
      noteForDelivery,
      note,
      address,
      elderName,
      elderPhoneNumber,
      elderHealthTags,
      deliveryBy,
      mealItems,
      elderId: selectedElderId,
      mealPrice,
    } = order;

    let elderId = selectedElderId;
    let needCreateElder = false;
    let needUpdateElder = false;
    if (elderId) {
      const existingElders = await asyncListAll(getEldersByClientByStatus, {
        clientId,
        status: { eq: '使用中' },
      });
      const matched = existingElders.find(({ id }) => id === elderId);
      if (matched) {
        if (!compareObjects(matched.address, address)) {
          needCreateElder = true;
        } else if (matched.name !== elderName) {
          needCreateElder = true;
        } else if (matched.noteForDelivery !== noteForDelivery ||
          matched.phoneNumber !== elderPhoneNumber
        ) {
          needUpdateElder = true;
        }
      } else {
        // 剛好被停用？
        needUpdateElder = true;
      }
    } else {
      needCreateElder = true;
    }
    if (needUpdateElder) {
      await request(updateElder, {
        input: {
          id: elderId,
          updatedBy: username,
          phoneNumber: elderPhoneNumber,
          noteForDelivery,
        },
      }, 'AMAZON_COGNITO_USER_POOLS');
    } else if (needCreateElder) {
      const { data: { createElder: { id } } } = await request(createElder, {
        input: {
          status: '使用中',
          clientId,
          county: address.county,
          createdBy: username,
          updatedBy: username,
          username: 'N/A',
          name: elderName,
          phoneNumber: elderPhoneNumber,
          address,
          noteForDelivery,
          note,
          isAllowedToEnterHome: 0,
          isDisadvantaged: 0,
        },
      }, 'AMAZON_COGNITO_USER_POOLS');

      elderId = id;

      // Update elder tags
      global.logger.debug('elderHealthTags', elderHealthTags);
      await Promise.all((elderHealthTags || []).map(async (tagItem) => {
        if (tagItem && tagItem.id) {
          const joinData = {
            tagId: tagItem.id,
            elderId,
            createdBy: username,
            updatedBy: username,
          };

          await request(createElderTag, { input: joinData });
        }
      }));
    }

    const getInvoiceSetting = () => {
      if (!supportedPaymentService.giveme || !transactionId) { // cash payment has no transactionId
        return;
      }
      const { type, email, carrier, title, taxIdNumber } = invoice;
      return {
        type,
        email,
        carrier: (type === 'individual')? carrier : '',
        title: type === 'enterprise' ? title : '',
        taxIdNumber: type === 'enterprise' ? taxIdNumber : '',
      };
    };

    const input = {
      orders: [{
        orderQuoteId,
        restaurantId,
        elderId,
        category: '送餐',
        deliveryDatetimes: [deliveryBy],
        noteForDelivery,
        note,
        total: mealPrice,
        mealItems: mealItems.map((mealItem) => ({
          name: mealItem.name,
          price: mealItem.price,
          quantity: mealItem.quantity,
        })),
      }],
      invoiceSetting: getInvoiceSetting(),
      transactionId: transactionId,
      individualId: clientId,
      facilityId: restaurant.clientId,
      source: 'groupPurchase',
    };
    const res = await request(individualClientCreateOrder, { input });
    global.logger.debug(res);

    // 顯示訂單送出成功訊息
    setShowNotificationDialog(true);

    cache.purge('public:menu-');
    setIsLoading(false);
  };

  const handleNotificationConfirm = () => {
    setShowNotificationDialog(false);
    history.push(`/client/myOrders`);
  };

  const isLoggedIn = cache.get('app:username') ? true : false;
  useEffect(() => {
    if (isLoggedIn) {
      const email = cache.get('app:email');
      if (invoice.email === '') {
        setInvoice({
          ...invoice,
          email,
        });
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!order || isLoading || !restaurant) return;

    const { restaurantId, address } = order;

    if (order.errorMessage &&
      order.quoteParams &&
      order.quoteParams.addressCounty === address.county &&
      order.quoteParams.addressZipCode === address.zipCode &&
      order.quoteParams.addressDistrict === address.district &&
      order.quoteParams.addressStreet === address.street &&
      order.quoteParams.restaurantId === restaurantId
    ) {
      return;
    }

    if (order.distance) return;

    (async () => {
      setIsLoading(true);

      const params = {
        input: {
          clientId: restaurant.clientId, // TODO
          restaurantId,
          addressCounty: address.county,
          addressZipCode: address.zipCode,
          addressDistrict: address.district,
          addressStreet: address.street,
        },
      };
      const {
        data: {
          quoteOrder: {
            data: {
              orderQuoteId,
              code,
              distance,
              deliveryStaffFee,
              startAddress,
              endAddress,
              message,
            },
          },
        },
      } = await request(quoteOrder, params);

      if (code === 'SUCCESS') {
        updateOrder({
          orderQuoteId,
          distance,
          deliveryStaffFee,
          startAddress,
          endAddress,
          subTotal: order.mealPrice + deliveryStaffFee,
          errorMessage: '',
          quoteParams: {},
        });
      } else {
        global.logger.debug({ params, message });
        updateOrder({
          errorMessage: '送餐地址有誤或距離過遠，請確認送餐地址或重新選擇餐點。',
          quoteParams: params,
        });
      }

      setIsLoading(false);
    })();
  }, [order, restaurant]);

  useEffect(() => {
    if (!restaurant) return;

    (async () => {
      const { data: { getSupportedPaymentService: { data } } } = await request(getSupportedPaymentService, {
        input: {
          facilityId: restaurant.clientId,
          type: 'groupPurchase',
        },
      });
      setSupportedPaymentService(JSON.parse(data));
    })();
  }, [restaurant]);

  useEffect(() => {
    (async () => {
      const {
        id: elderId,
        name: elderName,
        phoneNumber: elderPhoneNumber,
        address,
        noteForDelivery,
        noteForMeal,
        noteForHealth,
        date,
        timeSlot,
      } = cache.get('public:menu-elder');
      const {
        restaurantId,
        mealItems,
        restaurantName,
      } = cache.get('public:menu-meals');
      const existingOrder = cache.get('public:menu-order') || {};

      if (existingOrder.restaurantId !== restaurantId ||
        existingOrder.address.lat !== address.lat ||
        existingOrder.address.lng !== address.lng) {
        existingOrder.distance = undefined;
        existingOrder.deliveryStaffFee = 0;
      }
      const mealPrice = mealItems.reduce((pre, { price, quantity }) => pre + (price * quantity), 0);
      const order = {
        distance: undefined,
        deliveryFee: 0,
        startAddress: '',
        endAddress: '',
        ...existingOrder,
        date,
        timeSlot,
        deliveryBy: moment(`${date} ${timeSlot.split('-')[0]}`).tz(TIME_ZONE).toISOString(),
        elderId,
        elderName: elderName || '',
        elderPhoneNumber: elderPhoneNumber || '',
        noteForDelivery,
        noteForMeal,
        noteForHealth,
        restaurantId,
        restaurantName,
        address,
        mealItems,
        mealPrice,
        subTotal: mealPrice + (existingOrder.deliveryStaffFee || 0),
        errorMessage: '',
      };

      if (!order.elderName) {
        const clientId = cache.get('app:clientId');
        if (clientId) {
          const elders = await asyncListAll(getEldersByClientByStatus, {
            clientId,
            status: { eq: '使用中' },
          });
          if (elders.length > 0) {
            order.elderName = elders[0].name;
            order.elderPhoneNumber = elders[0].phoneNumber;
          }
        }
      }

      setOrder(order);
      setDate(date);
      setTimeSlot(timeSlot);

      const existingInvoice = cache.get('public:menu-invoice');
      if (existingInvoice) {
        setInvoice({ invoice, ...existingInvoice });
      }
      await getRestaurantInfo(restaurantId);
    })();
  }, []);

  useEffect(() => {
    if (!order) return;

    const elderCache = cache.get('public:menu-elder');
    const orderCache = cache.get('public:menu-order');

    if (elderCache.date !== orderCache?.date) {
      updateOrderDate(elderCache.date);
    }

    if (elderCache.timeSlot !== orderCache?.timeSlot) {
      updateOrderTimeSlot(elderCache.timeSlot);
    }
  }, [order]);

  const onInvoiceChange = (invoice) => {
    setInvoice(invoice);
    cache.set('public:menu-invoice', invoice);
  };

  if (!order) return null;

  // global.logger.debug('order', order);

  const invoiceValid = () => {
    if (!supportedPaymentService.giveme) {
      return true;
    }
    if (invoice.type === 'individual') {
      if (invoice.email.length === 0) {
        return false;
      }
    } else if (invoice.type === 'enterprise') {
      if (invoice.email.length === 0 || invoice.title.length === 0 || invoice.taxIdNumber.length === 0) {
        return false;
      }
    }
    return true;
  };


  const formDataInvalid = (type) => {
    if (type === 'offline') {
      return isLoading || !order.elderName || !order.elderPhoneNumber || !agreeTerms;
    }
    return isLoading || !order.elderName || !order.elderPhoneNumber || !agreeTerms ||
      (!agreeCancelInvoice && supportedPaymentService.giveme) || !invoiceValid();
  };

  return (
    <Grid container spacing={2} style={{ padding: 16 }}>
      {/* {order.startAddress && order.endAddress && <Grid container justifyContent="center">
        <Directions startAddress={order.startAddress} endAddress={order.endAddress} />
      </Grid>} */}
      <Grid item xs={12}>
        <Typography variant="h5" color="textPrimary" >
          訂購內容
        </Typography>
      </Grid>
      <Grid item xs={12} container justifyContent="space-between">
        <Typography variant="h6" color="textPrimary" >
          送餐地址
        </Typography>
        <Typography variant="h6" color="textPrimary" >
          {formatAddress(order.address, { ignoreCountry: true, includeZipCode: false, ignoreNote: true })}
        </Typography>
      </Grid>
      <Grid item xs={12} container justifyContent="space-between">
        <Typography variant="h6" color="textPrimary" >
          {t('餐廳')}
        </Typography>
        <Typography variant="h6" color="textPrimary" >
          {order.restaurantName}
        </Typography>
      </Grid>
      <Grid item xs={12} container style={{ borderBottom: '1px solid #e3e3e3' }}>
        {order.mealItems.map(({ name, price, quantity }) => (
          <Grid item xs={12} container justifyContent="space-between" key={name}>
            <Typography variant="h6" color="textSecondary" >
              {name} x {quantity}
            </Typography>
            <Typography variant="h6" color="textSecondary" >
              ${price * quantity}
            </Typography>
          </Grid>
        ))}
      </Grid>
      <Grid item xs={12} container justifyContent="space-between">
        <Typography variant="h6" color="textSecondary" >
          商品小計
        </Typography>
        <Typography variant="h6" color="textSecondary" >
          ${order.mealPrice}
        </Typography>
      </Grid>
      <Grid item xs={12} container justifyContent="space-between">
        <Typography variant="h6" color="textSecondary" >
          {t('配送費用')}
        </Typography>
        {order.distance ? <Typography variant="h6" color="textSecondary" >
          {`$${order.deliveryStaffFee}`}
        </Typography> : <Typography variant="h6" color="error" >
          {'計算中'}
        </Typography>}
      </Grid>
      <Grid item xs={12} container justifyContent="space-between">
        <Typography variant="h6" color="textPrimary" >
          訂單總金額
        </Typography>
        <Typography variant="h6" color="textPrimary" >
          ${order.subTotal}
        </Typography>
      </Grid>
      {order.errorMessage &&
      <>
        <Grid item xs={12} container justifyContent="center">
          <span style={{ color: Colors.raised }}>
            {order.errorMessage}
          </span>
        </Grid>
        <BackToMenuButton onStepBack={onStepBack} isLoading={isLoading} />
      </>
      }
      {!order.errorMessage &&
      <>
        <Grid item xs={12} style={{ paddingTop: 32 }}>
          <Typography variant="h5" color="textPrimary" >
            送餐時間
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <InputLabel id="date-label" style={{ fontSize: '1.2rem' }}>
              日期
          </InputLabel>
          <TextField
            fullWidth
            value={order.date}
            disabled
            inputProps={{
              style: { fontSize: '1.2rem' },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel id="date-label" style={{ fontSize: '1.2rem' }}>
              時段
          </InputLabel>
          <TextField
            fullWidth
            value={order.timeSlot}
            disabled
            inputProps={{
              style: { fontSize: '1.2rem' },
            }}
          />
        </Grid>
        <Grid item xs={12} style={{ paddingTop: 32 }}>
          <Typography variant="h5" color="textPrimary" >
            送餐資料
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="據點名稱/收餐者姓名"
            type="text"
            value={order.elderName}
            onChange={(e) => {
              updateOrder({ elderName: e.target.value });
              const cachedElder = cache.get('public:menu-elder');
              cache.set('public:menu-elder', { ...cachedElder, name: e.target.value });
            }}
            fullWidth
            required
            error={!order.elderName}
            helperText={!order.elderName ? '必填' : ''}
            inputProps={{
              style: { fontSize: '1.2rem' },
            }}
            InputLabelProps={{
              style: { fontSize: '1.4rem' },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={t('聯絡電話')}
            type="text"
            value={order.elderPhoneNumber}
            onChange={(e) => {
              updateOrder({ elderPhoneNumber: e.target.value });
              const cachedElder = cache.get('public:menu-elder');
              cache.set('public:menu-elder', { ...cachedElder, phoneNumber: e.target.value });
            }}
            fullWidth
            required
            error={!order.elderPhoneNumber}
            helperText={!order.elderPhoneNumber ? '必填' : ''}
            inputProps={{
              style: { fontSize: '1.2rem' },
            }}
            InputLabelProps={{
              style: { fontSize: '1.4rem' },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label={`${t('送餐')}備註`}
            type="text"
            value={order.noteForDelivery}
            onChange={(e) => {
              updateOrder({ noteForDelivery: e.target.value });
              const cachedElder = cache.get('public:menu-elder');
              cache.set('public:menu-elder', { ...cachedElder, noteForDelivery: e.target.value });
            }}
            fullWidth
            multiline
            inputProps={{
              style: { fontSize: '1.2rem' },
            }}
            InputLabelProps={{
              style: { fontSize: '1.2rem' },
            }}
          />
        </Grid>
        {isLoggedIn && (
          <>
            {supportedPaymentService.giveme && (
              <Grid item xs={12} container justifyContent="flex-start" style={{ paddingTop: 32 }}>
                <Grid item xs={12}>
                  <Typography variant="h5" color="textPrimary" >
                    發票資料
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <InvoiceInput
                    data={invoice}
                    onChange={onInvoiceChange}
                    donationRecipient={supportedPaymentService.donationRecipient}
                    donationCode={supportedPaymentService.donationCode}
                  />
                </Grid>
              </Grid>
            )}
            <Grid item xs={12} container justifyContent="flex-start" style={{ paddingTop: 16 }}>
              {supportedPaymentService.giveme && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="agreeCancelInvoice"
                        checked={agreeCancelInvoice}
                        onChange={(e) => {
                          setAgreeCancelInvoice(e.target.checked);
                        }}
                      />}
                    label={
                      <div style={{ fontSize: 14 }}>
                        {`我同意辦理退貨時由${restaurant?.name || '銀色大門'}代為處理電子發票以加速退貨退款作業`}
                      </div>}
                  />
                </Grid>
              )}
              {!agreeCancelInvoice && supportedPaymentService.giveme && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="error" >
                    {`請您同意辦理退貨時由${restaurant?.name || '銀色大門'}代為處理電子發票以加速退貨退款作業`}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="agreeTerms"
                      checked={agreeTerms}
                      onChange={(e) => {
                        setAgreeTerms(e.target.checked);
                      }}
                    />}
                  label={
                    <div style={{ fontSize: '1.2rem' }}>
                      我同意
                      <Link
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener"
                        variant="subtitle1"
                        underline="always"
                      >
                        隱私權及保護政策
                      </Link>
                      與
                      <Link
                        href="/return-policy"
                        target="_blank"
                        rel="noopener"
                        variant="subtitle1"
                        underline="always"
                      >
                        退貨退款政策
                      </Link>
                    </div>}
                />
              </Grid>
              {!agreeTerms && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" color="error" >
                    請您同意隱私權及保護政策與退貨退款政策
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} container justifyContent="flex-start" style={{ paddingTop: 32 }}>
              <Typography variant="h5" color="textPrimary" >
                付款方式
              </Typography>
            </Grid>
            <Grid item xs={12} container>
              {supportedPaymentService.linepay && (
                <Grid item xs={12} container justifyContent="center">
                  <LinePayButton
                    disabled={formDataInvalid()}
                    order={{
                      subTotal: order.subTotal,
                      amount: order.subTotal,
                      discount: 0,
                      discountRule: undefined,
                      description: '網頁訂單',
                      category: '送餐',
                      items: [...order.mealItems.map(({ name, quantity, unitValue, price }) => ({
                        name,
                        qty: quantity,
                        unit: '份',
                        unitValue,
                        subTotal: price,
                      })),
                      {
                        name: '送餐費用',
                        qty: 1,
                        unit: '次',
                        unitValue: order.deliveryStaffFee,
                        subTotal: order.deliveryStaffFee,
                      }],
                    }}
                    redirectPath={'/menu'}
                    onComplete={handleComplete}
                    checkCurrentOrderAmount={checkCurrentOrderAmount}
                    reportLoading={(value) => {
                      setIsLoading(value);
                    }}
                  />
                </Grid>
              )}
              {supportedPaymentService.newebpay && (
                <Grid item xs={12} container justifyContent="center" style={{ paddingTop: 16 }}>
                  <NewebPayButton
                    disabled={formDataInvalid()}
                    order={{
                      subTotal: order.subTotal,
                      amount: order.subTotal,
                      discount: 0,
                      discountRule: undefined,
                      description: '網頁訂單',
                      category: '送餐',
                      items: [...order.mealItems.map(({ name, quantity, unitValue, price }) => ({
                        name,
                        qty: quantity,
                        unit: '份',
                        unitValue,
                        subTotal: price,
                      })),
                      {
                        name: '送餐費用',
                        qty: 1,
                        unit: '次',
                        unitValue: order.deliveryStaffFee,
                        subTotal: order.deliveryStaffFee,
                      }],
                    }}
                    redirectPath={'/menu'}
                    clientBackPath={'/menu'}
                    email={invoice.email}
                    onComplete={handleComplete}
                    checkCurrentOrderAmount={checkCurrentOrderAmount}
                    reportLoading={(value) => {
                      setIsLoading(value);
                    }}
                    facilityId={restaurant?.clientId}
                  />
                </Grid>
              )}
              {supportedPaymentService.cash && (
                <Grid item xs={12} container justifyContent="center" style={{ paddingTop: 16 }}>
                  <CashButton
                    disabled={formDataInvalid('offline') || !isLoggedIn}
                    onComplete={handleComplete}
                    checkCurrentOrderAmount={checkCurrentOrderAmount}
                    reportLoading={(value) => {
                      setIsLoading(value);
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </>
        )}
        <BackToMenuButton onStepBack={onStepBack} isLoading={isLoading} />
        {!isLoggedIn && (
          <Grid item xs={12} container justifyContent="center" style={{ paddingTop: 32 }}>
            <CustomAuthDialog
              mode= 'signIn'
              withButton={false}
              button={(props) => (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  style={{ fontSize: '1.2rem' }}
                  {...props}
                >
                  登入
                </Button>
              )}
            />
            &nbsp;
            <CustomAuthDialog
              mode= 'signUp'
              withButton={false}
              button={(props) => (
                <Button
                  variant="text"
                  color="primary"
                  size="large"
                  style={{ fontSize: '1.2rem' }}
                  {...props}
                >
                  還不是會員？點此註冊
                </Button>
              )}
            />
          </Grid>
        )}
      </>
      }
      <Dialog
        open={showNotificationDialog}
        onClose={() => setShowNotificationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h4" style={{ color: '#00913A', fontWeight: 'bold' }}>🎉訂單送出成功🎉</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" style={{ fontSize: 16 }}>
            目前僅為送出團購訂單申請，實際上訂單成立與否須依集單狀況與店家做最後確認，<span style={{ color: 'red', fontWeight: 'bold' }}>不保證一定成立！</span><br />
            訂單狀態更新會電郵通知，請隨時<span style={{ color: 'red', fontWeight: 'bold' }}>確認電子信箱</span>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNotificationConfirm} color="primary" variant="contained">
            確認
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

CompleteOrder.propTypes = {
  formData: PropTypes.object,
  onComplete: PropTypes.func,
  onStepBack: PropTypes.func,
};
